<template lang="html">
  <span class="range-slider" :class="{ disabled, 'add-on': prefix || suffix }">
    <span v-if="prefix" class="range-prefix">{{ prefix }}</span>
    <drag-helper
      v-bind:disabled="disabled"
      @dragstart="dragStart"
      @drag="drag"
      @dragend="dragEnd">
      <span ref="inner" class="range-slider-inner">
        <input class="range-slider-hidden" type="text" :name="name" :value="actualValue" :disabled="disabled">
        <span class="range-slider-rail"></span>
        <span class="range-slider-fill" :style="{ width: valuePercent + '%' }"></span>

        <span ref="knob"
              class="range-slider-knob"
              @focus="onFocus"
              @blur="onBlur"
              :style="{ left: valuePercent + '%' }"
              :tabindex="disabled ? -1 : 0">
          <slot name="knob"></slot>
          <span ref="tooltip" class="tooltip">
            <span>{{ actualValue }}</span>
          </span>
        </span>
      </span>
    </drag-helper>
    <span v-if="suffix" class="range-suffix">{{ suffix }}</span>
  </span>
</template>

<script>
  import DragHelper from './DragHelper'
  import { round } from './utils'

  export default {
    props: {
      name: String,
      value: [String, Number],
      prefix: [String],
      suffix: [String],
      disabled: {
        type: Boolean,
        default: false
      },
      min: {
        type: [String, Number],
        default: 0
      },
      max: {
        type: [String, Number],
        default: 100
      },
      step: {
        type: [String, Number],
        default: 1
      }
    },

    data () {
      return {
        actualValue: null,
        dragStartValue: null,
        focused: false,
      }
    },

    created () {
      const { _min: min, _max: max } = this;
      let defaultValue = Number(this.value);

      if (this.value == null || isNaN(defaultValue)) {
        if (min > max) {
          defaultValue = min
        } else {
          defaultValue = (min + max) / 2
        }
      }

      this.actualValue = this.round(defaultValue)
    },

    computed: {
      _min () {
        return Number(this.min)
      },

      _max () {
        return Number(this.max)
      },

      _step () {
        return Number(this.step)
      },

      valuePercent () {
        return (this.actualValue - this._min) / (this._max - this._min) * 100
      }
    },

    watch: {
      value (newValue) {
        const value = Number(newValue);
        if (newValue != null && !isNaN(value)) {
          this.actualValue = this.round(value)
        }
      },
      min () {
        this.actualValue = this.round(this.actualValue)
      },
      max () {
        this.actualValue = this.round(this.actualValue)
      }
    },

    methods: {
      onFocus() {
        this.focused = true;

        window.addEventListener("keydown", this.onKeyDown);
        window.addEventListener("keyup", this.onKeyUp);
      },

      onBlur() {
        this.focused = false;

        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("keyup", this.onKeyUp);
      },

      onKeyDown(e) {
        const lockedKeys = [
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
        ];

        if (window._.includes(lockedKeys, e.key)) {
          e.preventDefault();
        }
      },

      onKeyUp(e) {
        let newValue;

        switch (e.key) {
          // Increase.
          case "ArrowUp":
          case "ArrowRight":
            newValue = this.actualValue + this._step;

            if (newValue > this._max) {
              newValue = this._max;
            }

            this.actualValue = this.round(newValue);
            break;

          // Decrease.
          case "ArrowDown":
          case "ArrowLeft":
            newValue = this.actualValue - this._step;

            if (newValue < this._min) {
              newValue = this._min;
            }

            this.actualValue = this.round(newValue);
            break;

          case "Enter":
            this.emitChange(this.actualValue);
            break;
        }
      },

      dragStart (event, offset) {
        this.dragStartValue = this.actualValue;
        if (event.target === this.$refs.knob) {
          return
        }
        // If the click is out of knob, move it to mouse position
        this.drag(event, offset)
      },

      drag (event, offset) {
        const { offsetWidth } = this.$refs.inner;
        this.actualValue = this.round(this.valueFromBounds(offset.left, offsetWidth));
        this.emitInput(this.actualValue);
      },

      dragEnd (event, offset) {
        const { offsetWidth } = this.$refs.inner;
        this.actualValue = this.round(this.valueFromBounds(offset.left, offsetWidth));

        if (this.dragStartValue !== this.actualValue) {
          this.emitChange(this.actualValue);
        }
      },

      emitInput(value) {
        this.$emit('input', value)
      },

      emitChange(value) {
        this.$emit('change', value)
      },

      valueFromBounds (point, width) {
        return (point / width) * (this._max - this._min) + this._min
      },

      round (value) {
        return round(value, this._min, this._max, this._step)
      }
    },

    components: {
      DragHelper
    }
  }
</script>

<style lang="scss">
  $slider-height: 20px;
  $slider-width: 100%;
  $rail-height: 4px;
  $knob-size: 30px;

  $rail-color: #e2e2e2;
  $rail-fill-color: $color-pizazz;
  $knob-color: #fff;

  $knob-border: 1px solid #f5f5f5;
  $knob-shadow: 1px 1px rgba(0, 0, 0, 0.2);
  $knob-shadow-focus: 1px 1px 5px rgba($color-pizazz, 0.95);

  .range-slider {
    position: relative;
    display: inline-block;
    margin: 0 auto 1rem;
    padding: 0;
    height: $slider-height;
    width: $slider-width;

    &::after {
      content: '';
      display: table;
      clear: both;
    }

    @include screen(md) {
      margin: 0 2rem 0 1rem;
    }

    &.add-on {
      display: table;
      vertical-align: middle;
    }
  }

  .range-slider.disabled {
    opacity: 0.5;
  }

  .range-prefix,
  .range-suffix {
    display: block;
  }

  .range-prefix,
  .range-suffix {
    position: absolute;
    top: 2rem;
    font-size: 14px;
    font-weight: $font-weight-bold;
    white-space: nowrap;
  }

  .range-suffix {
    right: 0;
  }

  .range-slider-inner {
    display: inline-block;
    position: relative;
    height: 100%;
    width: 100%;
  }

  @include screen(md) {
    .range-prefix,
    .range-suffix {
      position: static;
      top: auto;
      right: auto;
      display: table-cell;
      width: 14%;
      vertical-align: top;
    }

    .range-prefix {
      padding-right: 3%;
    }

    .range-suffix {
      padding-left: 3%;
    }

    .range-slider.add-on {
      .range-slider-inner {
        display: table-cell;
        width: 70%;
      }
    }
  }

  .range-slider-rail,
  .range-slider-fill {
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    height: $rail-height;
    border-radius: $rail-height / 2;
    transform: translateY(-50%);
  }

  .range-slider-rail {
    width: 100%;
    background-color: $rail-color;
  }

  .range-slider-fill {
    background-color: $rail-fill-color;
  }

  .range-slider-knob {
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    box-sizing: border-box;
    height: $knob-size;
    width: $knob-size;
    border: $knob-border;
    border-radius: 50%;
    background-color: $knob-color;
    box-shadow: $knob-shadow;
    transform: translate(-50%, -50%);
    cursor: pointer;

    &:focus {
      outline: 0;
      box-shadow: $knob-shadow-focus;
    }
  }

  .range-slider-hidden {
    display: none;
  }

  .tooltip {
    position: absolute;
    top: -25px;
    left: 2px;
    display: block;
    border-radius: 50% 50% 50% 0;
    background: $color-pizazz;
    color: #ffffff;
    width: 25px;
    height: 25px;
    transform: rotate(-45deg);
    box-shadow: 0 0 7px rgba(0,0,0,.25);

    span {
      display: block;
      transform: rotate(45deg);
      width: 100%;
      line-height: 23px;
      padding-top: 2px;
      padding-right: 1px;
      text-align: center;
      font-size: $font-size-tiny;
      font-weight: bolder;
    }
  }
</style>
